import { Col } from "components/primitives";
import { FC, useEffect, useRef, useState } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import styled from "styled-components";
import { Vector2 } from "types/Vector2";
import { elementsOverlap } from "util/elementsOverlay";

interface Props {
  header: string;
  color: string;
  value: number;
  showNumbers: boolean;
  scale: Vector2;
  imgSrc: string;
  size: Vector2;
  columnRatio: number;
  blockPositions: Vector2[];
  setBlockPositions: (positions: Vector2[]) => void;
  isColumnActive: boolean;
  setColumnActive: (key: string | undefined) => void;
  columnWidth: number;
}

function vectorMag(a: Vector2, b: Vector2): number {
  return Math.abs(Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2)));
}

export const MABColumn: FC<Props> = ({
  blockPositions,
  setBlockPositions,
  header,
  color,
  value,
  scale,
  showNumbers,
  imgSrc,
  size,
  columnRatio,
  isColumnActive,
  setColumnActive,
  columnWidth,
}) => {
  const dropCellRef = useRef<HTMLDivElement>(null);
  const baseCellRef = useRef<HTMLDivElement>(null);
  const [baseBlockPosition, setBaseBlockPosition] = useState<Vector2>({
    x: 0,
    y: 0,
  });
  const blockSize: Vector2 = { x: size.x * scale.y, y: size.y * scale.y };
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    setBaseBlockPosition(getDefaultBasePosition());
  }, [scale]);

  const overlapWithDropZone = (element: HTMLElement): boolean => {
    if (dropCellRef.current) {
      return elementsOverlap(dropCellRef.current, element);
    } else {
      return false;
    }
  };

  function addNewBlockPosition(position: Vector2) {
    if (blockPositions.length < 20) {
      let newBlockPositions = [...blockPositions];
      newBlockPositions.push(position);
      setBlockPositions(newBlockPositions);
    }
  }

  function clampPositionToRect(position: Vector2, node: HTMLElement): Vector2 {
    let clamped: Vector2 = position;
    if (dropCellRef.current) {
      const dropRect = dropCellRef.current.getBoundingClientRect();
      const elRect = node.getBoundingClientRect();
      if (position.x + elRect.width > dropRect.width) {
        clamped = { x: dropRect.width - elRect.width, y: clamped.y };
      }
      if (position.y + elRect.height > dropRect.height) {
        clamped = { x: clamped.x, y: dropRect.height - elRect.height };
      }
      if (position.x < 0) {
        clamped = { x: 0, y: clamped.y };
      }
      if (position.y < 0) {
        clamped = { x: clamped.x, y: 0 };
      }
    }
    return clamped;
  }

  const onDragStopControlledBlock = (e: DraggableEvent, data: DraggableData, index: number) => {
    const moveDistance = vectorMag(
      { x: data.x / scale.x, y: data.y / scale.y },
      blockPositions[index],
    );
    if (moveDistance < 1 || !overlapWithDropZone(data.node)) {
      let newBlockPositions = [...blockPositions];
      newBlockPositions.splice(index, 1);
      setBlockPositions(newBlockPositions);
    } else {
      let newBlockPositions = [...blockPositions];
      const newPos = clampPositionToRect({ x: data.x, y: data.y }, data.node);
      newBlockPositions[index] = {
        x: newPos.x / scale.x,
        y: newPos.y / scale.y,
      };
      setBlockPositions(newBlockPositions);
    }

    setColumnActive(undefined);
    setDragging(false);
  };

  const dragStop = (e: DraggableEvent, data: DraggableData) => {
    console.log("drag stop");

    const moveDistance = vectorMag({ x: data.x / scale.x, y: data.y / scale.y }, baseBlockPosition);
    if (overlapWithDropZone(data.node) && dropCellRef.current) {
      const dropCellRect = dropCellRef.current.getBoundingClientRect();
      const newPosition = clampPositionToRect(
        { x: data.x, y: data.y + dropCellRect.height },
        data.node,
      );
      addNewBlockPosition({
        x: newPosition.x / scale.x,
        y: newPosition.y / scale.y,
      });
    } else if (moveDistance < 1) {
      addNewBlockPosition({
        x: ((columnWidth * (columnRatio / 4)) / scale.x) * (blockPositions.length % 3) * 0.2,
        y: Math.floor(blockPositions.length / 3) * size.y,
      });
    }

    setBaseBlockPosition(getDefaultBasePosition());
    setColumnActive(undefined);
    setDragging(false);
  };

  const onDragStart = (e: DraggableEvent, data: DraggableData) => {
    setColumnActive(header);
    setDragging(true);
  };

  function getDefaultBasePosition(): Vector2 {
    if (baseCellRef.current) {
      const baseRect = baseCellRef.current.getBoundingClientRect();
      // The item size is 20% of the container width (defined in css)
      // so we use that to determine the midpoint
      return {
        x: baseRect.width / scale.x / 2 - blockSize.x / 2 / scale.x,
        y: baseRect.height / scale.y / 2 - blockSize.y / 2 / scale.y,
      };
    } else {
      return { x: 0, y: 0 };
    }
  }

  function scaledPosition(position: Vector2): Vector2 {
    return {
      x: position.x * scale.x,
      y: position.y * scale.y,
    };
  }

  return (
    <InnerCol columnRatio={columnRatio} isActive={isColumnActive}>
      <HeaderCell>
        <p>{header}</p>
        {showNumbers && <ResultingNumber>{value}</ResultingNumber>}
      </HeaderCell>
      <DropCell ref={dropCellRef} isActive={dragging}>
        {Boolean(blockPositions.length) &&
          blockPositions.map((position, index) => {
            return (
              <Draggable
                key={index}
                position={scaledPosition(position)}
                onDrag={(e, d) => onDragStart(e, d)}
                onStop={(e, d) => onDragStopControlledBlock(e, d, index)}
              >
                <DragBlock
                  width={`${blockSize.x}px`}
                  aspect={size.x / size.y}
                  color={color}
                  style={{ backgroundImage: `url(${imgSrc})` }}
                />
              </Draggable>
            );
          })}
      </DropCell>
      <BaseCell ref={baseCellRef} isActive={dragging}>
        <Draggable
          position={scaledPosition(baseBlockPosition)}
          onDrag={(e, d) => onDragStart(e, d)}
          onStop={dragStop}
        >
          <DragBlock
            width={`${blockSize.x}px`}
            aspect={size.x / size.y}
            color={color}
            style={{ backgroundImage: `url(${imgSrc})` }}
          />
        </Draggable>
      </BaseCell>
    </InnerCol>
  );
};

const InnerCol = styled(Col)<{ columnRatio: number; isActive: boolean }>`
  flex-grow: ${(props) => props.columnRatio};
  container: block-column / size;
  z-index: ${(props) => (props.isActive ? "200" : "100")};
  position: relative;
`;

const HeaderCell = styled(Col)`
  border: 1px solid black;
  height: 20%;
  justify-content: center;
  align-items: center;
  color: var(--widget-primary);
  font-weight: 600;
  font-size: 5cqh;
  gap: 3cqh;

  p {
    line-height: 1em;
  }
`;

const DropCell = styled.div<{ isActive: boolean }>`
  border: 1px solid black;
  height: 60%;
  overflow-y: auto;
  position: relative;
  z-index: ${(props) => (props.isActive ? "110" : "120")};
  background-color: ${(props) => (props.isActive ? "rgba(0, 0, 0, 0.1)" : "none")};
`;

const BaseCell = styled(Col)<{ isActive: boolean }>`
  border: 1px solid black;
  height: 20%;
  background-color: #eaf0ff;
  position: relative;
  z-index: ${(props) => (props.isActive ? "120" : "110")};
`;

const DragBlock = styled.div.attrs<{ width: string }>((props) => ({
  style: { width: props.width },
}))<{
  color: string;
  width: string;
  aspect: number;
}>`
  aspect-ratio: ${(props) => props.aspect};
  position: absolute;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 150;

  &:hover {
    filter: brightness(90%);
  }
`;

const ResultingNumber = styled(Col)`
  border-radius: 50px;
  background-color: #eaf0ff;
  font-size: 4cqh;
  padding: 2cqh 3cqh;
  min-width: 15cqh;
  text-align: center;
  font-weight: 600;
  color: var(--secondary);
  height: 35%;
  justify-content: center;
  align-items: center;
`;
